import moment from "moment";
import { useEffect, useState } from "react";
import Calendar from "react-awesome-calendar";
import { Button, Modal } from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useTranslation } from "react-i18next";
import { AiFillDollarCircle } from "react-icons/ai";
import { FaBus, FaCalendarAlt } from "react-icons/fa";
import { IoCloseSharp } from "react-icons/io5";
import ReactPaginate from "react-paginate";
import SkeletonLoading from "../../component/SkeletonLoading";
import i18n from "../../store/api/i18n";
import {
  useGetCalendarParentQuery,
  useGetDashboardParentQuery,
  useGetDashboardParentStatsQuery,
} from "../../store/api/parent/dashboardParentSlice";
import { formatOfDate } from "../../utils/dateAndTimeConversion";
import ReservationDetail from "../SchoolDashboard/ReservationDetail";

const initialDate = {
  startDate: "",
  endDate: "",
};

const initialPagination = {
  limit: 10,
  page: 1,
  total: 0,
  totalPages: 1,
};

const ParentDashboard = () => {
  const { t } = useTranslation();
  const [date, setDate] = useState(initialDate);
  const [pagination, setPagination] = useState(initialPagination);
  const [dashboardStats, setDashboardStats] = useState({
    monthly_payments: 0,
    overdue_payments: 0,
    total_children: 0,
  });

  const [reservedVehicles, setReservedVehicles] = useState([]);
  const [eventsCalender, setEventsCalender] = useState([]);
  const [show3, setShow3] = useState(false);
  const [bookingIdData, setBookingIdData] = useState();
  const handleClose3 = () => setShow3(false);

  // Get Dashboard Stats
  const { data, isSuccess } = useGetDashboardParentStatsQuery({
    startDate: date.startDate,
    endDate: date.endDate,
  });

  // Get Reserved Vehicles
  const {
    data: dashboardData,
    isSuccess: isDashboardSuccess,
    isLoading: isDashboardLoading,
    isFetching: isDashboardFetching,
    refetch,
  } = useGetDashboardParentQuery({
    ...date,
    ...pagination,
    startDate: date.startDate,
    endDate: date.endDate,
  });

  // Get Calendar Events
  const { data: calendarData, isSuccess: isCalendarSuccess } =
    useGetCalendarParentQuery({
      startDate: date.startDate,
      endDate: date.endDate,
    });

  // Fetch Dashboard Stats
  useEffect(() => {
    if (isSuccess && data?.data) {
      setDashboardStats(data.data);
    }
  }, [data, isSuccess]);

  // Fetch Dashboard Reserved Vehicles
  useEffect(() => {
    if (isDashboardSuccess && dashboardData?.data) {
      setReservedVehicles(dashboardData.data.data);
      setPagination((prev) => ({
        ...prev,
        total: dashboardData?.data.pagination.total,
        page: dashboardData?.data.pagination.page,
        limit: dashboardData?.data.pagination.limit,
        totalPages: dashboardData?.data.pagination.pages,
      }));
    }
  }, [dashboardData, isDashboardSuccess]);

  // Get Calendar Events
  useEffect(() => {
    if (isCalendarSuccess && calendarData?.data) {
      const formattedEvents = calendarData.data.data.map((event) => ({
        ...event,
        from: new Date(event.from).toISOString(),
        to: new Date(event.to).toISOString(),
      }));
      setEventsCalender(formattedEvents);
    }
  }, [calendarData, isCalendarSuccess]);

  // Language Change Handler
  useEffect(() => {
    const h = () => {
      refetch?.();
    };
    i18n.on("languageChanged", h);
    return () => i18n.off("languageChanged", h);
  }, [refetch]);

  const handleCalendar = (data) => {
    const { year, month } = data;
  };

  const handleDateChange = (selectedDate, field) => {
    setDate((prev) => {
      const adjustedDate = moment(selectedDate).startOf("day").toDate();
      const formattedDate = adjustedDate ? formatOfDate(adjustedDate) : "";
      const newDates = { ...prev, [field]: formattedDate };

      if (
        field === "startDate" &&
        newDates.endDate &&
        formattedDate > newDates.endDate
      ) {
        newDates.endDate = "";
      }

      return newDates;
    });
  };

  const handlePageChange = (selectedItem) => {
    const selectedPage = selectedItem.selected + 1;
    setPagination((prev) => ({ ...prev, page: selectedPage }));
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items-center flex-wrap w-100 mb-2">
        {/* Filters */}
        <div className="d-flex flex-wrap gap-2">
          <div className="d-flex flex-grow-1">
            <div className="position-relative w-100">
              <DatePicker
                className="form-control"
                selected={
                  date.startDate
                    ? new Date(moment(date.startDate, "YYYY-MM-DD").toDate())
                    : null
                }
                onChange={(selectedDate) =>
                  handleDateChange(selectedDate, "startDate")
                }
                selectsStart
                startDate={
                  date.startDate
                    ? new Date(moment(date.startDate, "YYYY-MM-DD").toDate())
                    : null
                }
                endDate={date.endDate ? new Date(date.endDate) : null}
                dateFormat="yyyy-MM-dd"
                placeholderText={`${t("select")} ${t("start")} ${t("date")}`}
              />
              <FaCalendarAlt className="calederIcon" />
            </div>
          </div>

          <div className="d-flex flex-grow-1">
            <div className="position-relative w-100">
              <DatePicker
                className="form-control"
                selected={
                  date.endDate
                    ? moment(date.endDate, "YYYY-MM-DD").toDate()
                    : null
                }
                onChange={(selectedDate) =>
                  handleDateChange(selectedDate, "endDate")
                }
                selectsEnd
                startDate={
                  date.startDate
                    ? moment(date.startDate, "YYYY-MM-DD").toDate()
                    : null
                }
                endDate={
                  date.endDate
                    ? moment(date.endDate, "YYYY-MM-DD").toDate()
                    : null
                }
                minDate={
                  date.startDate
                    ? moment(date.startDate, "YYYY-MM-DD").toDate()
                    : new Date()
                }
                dateFormat="yyyy-MM-dd"
                placeholderText={`${t("select")} ${t("end")} ${t("date")}`}
                disabled={!date.startDate}
              />
              <FaCalendarAlt className="calederIcon" />
            </div>
          </div>
        </div>
      </div>

      <ul className="dbTop p-0 d-flex flex-wrap">
        <li className="col-12 col-sm-4">
          <div className="inner" style={{ background: "#FBF7F3" }}>
            <div className="hd d-flex align-items-center mb-3">
              <span>
                <AiFillDollarCircle className="icon me-2" />
              </span>
              {t("monthly")} {t("payments")}
            </div>
            <div className="d-flex align-items-center">
              <div className="price col-6">
                <span className="d-block mb-1">
                  ${dashboardStats?.monthly_payments?.toLocaleString() || 0}
                </span>
              </div>
            </div>
          </div>
        </li>

        <li className="col-12 col-sm-4">
          <div className="inner" style={{ background: "#F8F9FB" }}>
            <div className="hd d-flex align-items-center mb-3">
              <span>
                <AiFillDollarCircle className="icon me-2" />
              </span>
              {t("outstanding")} {t("payments")}
            </div>
            <div className="d-flex align-items-center">
              <div className="price col-6">
                <span className="d-block mb-1">
                  ${dashboardStats?.overdue_payments?.toLocaleString() || 0}
                </span>
              </div>
            </div>
          </div>
        </li>

        <li className="col-12 col-sm-4">
          <div className="inner" style={{ background: "#E9FDF0" }}>
            <div className="hd d-flex align-items-center mb-3">
              <span>
                <FaBus className="icon me-2" />
              </span>
              {t("childs")}
            </div>
            <div className="d-flex align-items-center">
              <div className="price">
                <span className="d-block mb-1">
                  {dashboardStats?.total_children || 0}
                </span>
              </div>
            </div>
          </div>
        </li>
      </ul>

      <div className="row pt-4">
        <div className="col-lg-12 col-12">
          <div className="d-flex align-items-center justify-content-between">
            <div className="dbSubHd mb-2">
              {t("vehicles")} {t("on")} {t("board")}
            </div>
          </div>

          <div className="table m-0">
            <table>
              <thead>
                <tr>
                  <th>
                    {t("Sr")}.{t("no")}.
                  </th>
                  <th>
                    {t("company")} {t("name")}
                  </th>
                  <th>
                    {t("vehicle")} {t("type")}
                  </th>
                  <th>
                    {t("license")} {t("no")}
                  </th>
                  <th>{t("status")}</th>
                </tr>
              </thead>
              <tbody>
                {isDashboardLoading || isDashboardFetching ? (
                  <SkeletonLoading MainLength={10} length={5} />
                ) : reservedVehicles?.length > 0 ? (
                  reservedVehicles.map((item, i) => (
                    <tr key={i}>
                      <td>
                        {(pagination.page - 1) * pagination.limit + i + 1}
                      </td>
                      <td className="text-nowrap">{item?.vendor_name}</td>
                      <td className="text-nowrap">{item?.vehicle_type_name}</td>
                      <td className="text-nowrap">{item?.license_number}</td>
                      <td className="text-nowrap">
                        <span
                          className={`status ${item?.vehicle_status || "unavailable"}`}
                        >
                          {item?.vehicle_status === "scheduled"
                            ? "Reserved"
                            : item?.vehicle_status
                              ? item.vehicle_status.charAt(0).toUpperCase() +
                                item.vehicle_status.slice(1)
                              : "N/A"}
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr className="text-center">
                    <td colSpan="5">
                      <div>
                        <span>
                          {t("no")} {t("reserved")} {t("vehicles")} {t("found")}
                        </span>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        <ul className="paging m-0 p-0 pt-3 d-flex">
          <li>
            <ReactPaginate
              pageCount={pagination.totalPages}
              onPageChange={handlePageChange}
              containerClassName={"paging m-0 p-0 pt-3 d-flex"}
              activeClassName={"active"}
              previousLabel={t("previous")}
              nextLabel={t("next")}
              selectedPage={pagination.page}
            />
          </li>
        </ul>

        <div className="mt-4 outerBox">
          <div className="dbSubHd mb-3">
            {t("reservations")} {t("calendar")}
          </div>
          <div className="calenderBox">
            <Calendar
              onChange={handleCalendar}
              events={eventsCalender}
              onClickEvent={(data) => {
                setShow3(true);
                setBookingIdData(data);
              }}
            />
          </div>
        </div>
      </div>

      <Modal
        className="addbankDetails"
        show={show3}
        onHide={handleClose3}
        centered
      >
        <Button className="closePopup" onClick={handleClose3}>
          <IoCloseSharp />
        </Button>
        <ReservationDetail
          bookingIdData={bookingIdData}
          onClose={handleClose3}
        />
      </Modal>
    </>
  );
};

export default ParentDashboard;
